
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { safeReload } from '@/utils/navigation';
import { PRODUCTION_CONFIG } from '@/config/production';
interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  isDispatcherError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    isDispatcherError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    const isDispatcherError = error.message?.includes('dispatcher.useState') || 
                             error.message?.includes('dispatcher.useEffect') ||
                             error.message?.includes('dispatcher.useContext');
    
    return { 
      hasError: true, 
      error, 
      errorInfo: null,
      isDispatcherError
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (PRODUCTION_CONFIG.monitoring.enableConsoleLogging) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
    this.setState({ errorInfo });
    
    const isDispatcherError = error.message?.includes('dispatcher.useState') || 
                             error.message?.includes('dispatcher.useEffect') ||
                             error.message?.includes('dispatcher.useContext');
    
    // Only auto-reload in development for dispatcher errors if we haven't done so recently
    if (isDispatcherError && import.meta.env.DEV) {
      const lastReload = localStorage.getItem('lastDevReload');
      const now = Date.now();
      
      // Only auto-reload if we haven't reloaded in the last 15 seconds
      if (!lastReload || (now - parseInt(lastReload, 10) > 15000)) {
        console.warn('React dispatcher error detected - scheduling auto-recovery');
        
        // Clear any problematic cache data
        try {
          sessionStorage.clear();
          localStorage.removeItem('forceCacheRefresh');
          localStorage.removeItem('lastReloadTime');
        } catch (e) {
          console.error('Error clearing cache during recovery:', e);
        }
        
        // Record reload time and schedule reload
        localStorage.setItem('lastDevReload', now.toString());
        
        setTimeout(() => {
          if (PRODUCTION_CONFIG.monitoring.enableConsoleLogging) {
            console.log('Auto-reloading due to dispatcher error');
          }
          safeReload('error-boundary-auto');
        }, 2000); // Longer delay to prevent rapid reloads
      } else {
        if (PRODUCTION_CONFIG.monitoring.enableConsoleLogging) {
          console.log('Skipping auto-reload due to recent reload');
        }
      }
    }
    
    // Log to an error tracking service if available
    if (window.navigator && navigator.onLine) {
      if (PRODUCTION_CONFIG.monitoring.enableConsoleLogging) {
        console.log('Would send to error tracking service:', {
          error: error.toString(),
          stack: error.stack,
          componentStack: errorInfo.componentStack,
          isDispatcherError
        });
      }
    } else {
      // Store in localStorage to send later when online
      try {
        const storedErrors = JSON.parse(localStorage.getItem('pendingErrors') || '[]');
        storedErrors.push({
          timestamp: Date.now(),
          error: error.toString(),
          stack: error.stack,
          isDispatcherError
        });
        localStorage.setItem('pendingErrors', JSON.stringify(storedErrors.slice(-10))); // Keep last 10 errors
      } catch (e) {
        console.error('Failed to store error for later reporting', e);
      }
    }
  }

  private handleReload = () => {
    // Clear cache data before reloading
    try {
      sessionStorage.clear();
      localStorage.removeItem('forceCacheRefresh');
      localStorage.removeItem('lastReloadTime');
      localStorage.removeItem('lastRefreshTime');
      localStorage.removeItem('lastDevReload');
    } catch (e) {
      console.error('Error clearing cache before reload:', e);
    }
    
    safeReload('error-boundary-manual');
  };

  public render() {
    if (this.state.hasError) {
      // Check if a custom fallback was provided
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      // Special handling for dispatcher errors
      if (this.state.isDispatcherError && import.meta.env.DEV) {
        return (
          <div className="win95-container min-h-screen flex flex-col items-center justify-center p-4 bg-win95-gray">
            <div className="bg-white p-6 border-2 border-black max-w-md w-full">
              <div className="flex items-center mb-4">
                <AlertTriangle className="h-8 w-8 text-orange-600 mr-3" />
                <h2 className="text-xl font-bold">Development Mode Recovery</h2>
              </div>
              
              <p className="mb-4 text-gray-700">
                A React hot reload error occurred. You can manually reload or wait for auto-recovery.
              </p>
              
              <div className="bg-orange-50 p-3 rounded mb-4 text-sm">
                <strong>For developers:</strong> This typically happens during hot module replacement. 
                The page may auto-reload in a few seconds, or you can reload manually.
              </div>
              
              <div className="flex justify-center">
                <Button 
                  onClick={this.handleReload}
                  className="flex items-center"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Reload Now
                </Button>
              </div>
            </div>
          </div>
        );
      }
      
      // Standard error display
      return (
        <div className="win95-container min-h-screen flex flex-col items-center justify-center p-4 bg-win95-gray">
          <div className="bg-white p-6 border-2 border-black max-w-md w-full">
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-8 w-8 text-red-600 mr-3" />
              <h2 className="text-xl font-bold">Something went wrong</h2>
            </div>
            
            <p className="mb-4 text-gray-700">
              The application has encountered an unexpected error. 
            </p>
            
            {this.state.error && (
              <div className="bg-gray-100 p-2 rounded my-4 overflow-auto max-h-32 text-xs">
                <code>{this.state.error.toString()}</code>
              </div>
            )}
            
            <div className="flex justify-center mt-4">
              <Button 
                onClick={this.handleReload}
                className="flex items-center"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Reload Application
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
